import { useEffect } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest, performPutRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useFlagsService, toFlagId } from '@atlassian/jira-flags';
import { createHook, createStore, type StoreActionApi } from '@atlassian/react-sweet-state';
import messages from './messages';
import { type SafeNotificationsSetting, SafeNotificationsOption } from './types';

type StoreState = {
	data: SafeNotificationsSetting | null;
	loading: boolean;
	error?: Error;
};

type Actions = typeof actions;
export const flagId = toFlagId('safe-notification-enabled-success-flag-key');
const LEARN_MORE_ABOUT_CUSTOMER_NOTIFICATION_LINK =
	'https://support.atlassian.com/jira-service-management-cloud/docs/what-are-the-compliance-settings/';

const actions = {
	fetchSafeNotificationsSetting:
		() =>
		async ({ setState, getState }: StoreActionApi<StoreState>) => {
			if (getState().loading === true) return;

			setState({
				...getState(),
				loading: true,
			});

			try {
				const data = await performGetRequest(
					'/rest/servicedesk/admin/1/compliance/safe-notification-setting',
				);
				setState({
					...getState(),
					data,
					loading: false,
					error: undefined,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState({
					...getState(),
					loading: false,
					error,
				});
			}
		},
	updateSafeNotificationsSetting:
		(option: (typeof SafeNotificationsOption)[keyof typeof SafeNotificationsOption]) =>
		async ({ setState, getState }: StoreActionApi<StoreState>) => {
			if (getState().loading === true) return;

			const { showFlag, dismissFlag } = useFlagsService();
			setState({
				...getState(),
				loading: true,
			});

			try {
				const newState = await performPutRequest(
					'/rest/servicedesk/admin/1/compliance/safe-notification-setting',
					{
						body: JSON.stringify({
							safeNotificationSetting: option,
						}),
					},
				);
				option === SafeNotificationsOption.ENABLED &&
					showFlag({
						id: flagId,
						title: messages.safeNotificationsEnabledSuccessTitle,
						type: 'success',
						description: messages.safeNotificationsEnabledSuccessDescription,
						actions: [
							{
								content: messages.flagOk,
								onClick: () => {
									dismissFlag(flagId);
								},
							},
							{
								content: messages.flagLearnMore,
								href: LEARN_MORE_ABOUT_CUSTOMER_NOTIFICATION_LINK,
								target: '_blank',
							},
						],
					});
				setState({
					...getState(),
					data: newState,
					loading: false,
					error: undefined,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					meta: {
						id: 'updateSafeNotificationsSetting',
						packageName: 'jiraProductsJsmComplianceMain',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
					attributes: {
						safeNotificationOption: option,
					},
				});
				setState({
					...getState(),
					loading: false,
					error,
				});
			}
		},
};

const Store = createStore<StoreState, Actions>({
	initialState: {
		data: null,
		loading: false,
		error: undefined,
	},
	actions,
	name: 'SafeNotificationsSetting',
});

export const useSafeNotificationsSettingData = createHook(Store);

export const useSafeNotificationsSettingWithInitialisation = () => {
	const [
		safeNotificationsSettingState,
		{ fetchSafeNotificationsSetting, updateSafeNotificationsSetting },
	] = useSafeNotificationsSettingData();

	useEffect(() => {
		if (!safeNotificationsSettingState.data) {
			fetchSafeNotificationsSetting();
		}
	}, [fetchSafeNotificationsSetting, safeNotificationsSettingState.data]);

	return {
		loading: safeNotificationsSettingState.loading,
		error: safeNotificationsSettingState.error,
		data: safeNotificationsSettingState.data,
		isSafeNotificationsEnabled:
			safeNotificationsSettingState.data?.safeNotificationSetting ===
			SafeNotificationsOption.ENABLED,
		enableSetting: () => updateSafeNotificationsSetting(SafeNotificationsOption.ENABLED),
		disableSetting: () => updateSafeNotificationsSetting(SafeNotificationsOption.DISABLED),
	};
};
